import React, { useState, useEffect } from 'react';
import { OpportunityStatus, TechnicalStatus } from '../models/opportunity';

export interface NovaOportunidadeForm {
  titulo: string;
  cliente: string;
  contactoCliente: string;
  comercialEmail: string;
  comercialNome: string;
  statusOportunidade: OpportunityStatus;
  ambitoNecessidades: string;
  tecnicoEmail: string;
  tecnicoNome: string;
  statusTecnico: TechnicalStatus;
  dataLimiteParecer: string;
}

interface Membro { nome: string; email: string; }

interface Props {
  currentUserEmail: string;
  currentUserName: string;
  onSubmit: (form: NovaOportunidadeForm) => Promise<void>;
  onClose: () => void;
  getMembers: () => Promise<Membro[]>;
  getNextCode: () => Promise<string>;
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0D0F1A', border: '1px solid #252B45',
  borderRadius: 8, padding: '9px 12px', color: '#D8DDEF', fontSize: 13,
  fontFamily: 'inherit', outline: 'none',
};
const labelStyle: React.CSSProperties = {
  fontSize: 11, color: '#8892AA', textTransform: 'uppercase',
  letterSpacing: 0.5, display: 'block', marginBottom: 5,
};
const col2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const sectionTitle = (color: string, icon: string, text: string) => (
  <div style={{ fontSize: 11, color, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, borderBottom: '1px solid #252B45', paddingBottom: 6 }}>
    {icon} {text}
  </div>
);

export const NovaOportunidadeModal: React.FC<Props> = ({ currentUserEmail, currentUserName, onSubmit, onClose, getMembers, getNextCode }) => {
  const [membros, setMembros] = useState<Membro[]>([]);
  const [loadingMembros, setLoadingMembros] = useState(true);
  const [loadingCode, setLoadingCode] = useState(true);

  // Lista de fallback (usada se API falhar)
  const FALLBACK: Membro[] = [
    { nome: 'Agnelo Penelas',            email: 'agnelo.pdc@chk.co.ao' },
    { nome: 'Artur Brito',               email: 'artur.pb@chk.co.ao' },
    { nome: 'Bertini BM. Morais',        email: 'Bertini.Morais@chk.co.ao' },
    { nome: 'Cadimiel Pedro',            email: 'cadimiel.p@chk.co.ao' },
    { nome: 'Delcio Cristovao',          email: 'delcio.isc@chk.co.ao' },
    { nome: 'Erica Gamboa',              email: 'erica.ptg@chk.co.ao' },
    { nome: 'Fernando Jaime',            email: 'fernando.jj@chk.co.ao' },
    { nome: 'Helio Dias Brito Teixeira', email: 'helio.dbt@chk.co.ao' },
    { nome: 'Lauro Monteiro',            email: 'lauro.sjm@chk.co.ao' },
    { nome: 'Manuel Miguel',             email: 'manuel.tm@chk.co.ao' },
    { nome: 'Renato Camenha',            email: 'renato.bc@chk.co.ao' },
    { nome: 'Silvio Makemba',            email: 'silvio.m@chk.co.ao' },
  ];

  // Buscar próximo código automaticamente
  useEffect(() => {
    getNextCode()
      .then(code => setForm(f => ({ ...f, titulo: code })))
      .catch(() => {})
      .finally(() => setLoadingCode(false));
  }, []);

  useEffect(() => {
    getMembers()
      .then(m => {
        const lista = m && m.length > 0 ? m : FALLBACK;
        setMembros(lista);
        // Pre-seleccionar comercial com o utilizador actual
        const eu = lista.find(x => x.email.toLowerCase() === currentUserEmail.toLowerCase());
        if (eu) {
          setForm(f => ({ ...f, comercialEmail: eu.email, comercialNome: eu.nome }));
        }
      })
      .catch(() => {
        setMembros(FALLBACK);
        const eu = FALLBACK.find(x => x.email.toLowerCase() === currentUserEmail.toLowerCase());
        if (eu) setForm(f => ({ ...f, comercialEmail: eu.email, comercialNome: eu.nome }));
      })
      .finally(() => setLoadingMembros(false));
  }, []);

  const defaultPrazo = new Date(Date.now() + 7 * 86400000).toISOString().substring(0, 10);

  const [form, setForm] = useState<NovaOportunidadeForm>({
    titulo: '',
    cliente: '',
    contactoCliente: '',
    comercialEmail: currentUserEmail,
    comercialNome: currentUserName,
    statusOportunidade: '2. Viabilidade Tecnica Pendente' as OpportunityStatus,
    ambitoNecessidades: '',
    tecnicoEmail: '',
    tecnicoNome: '',
    statusTecnico: 'Nao Iniciado' as TechnicalStatus,
    dataLimiteParecer: defaultPrazo,
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (k: keyof NovaOportunidadeForm, v: string) =>
    setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.titulo.trim()) e.titulo = 'Obrigatorio';
    if (!form.cliente.trim()) e.cliente = 'Obrigatorio';
    if (!form.comercialEmail) e.comercialEmail = 'Obrigatorio';
    if (!form.ambitoNecessidades.trim()) e.ambitoNecessidades = 'Obrigatorio';
    if (!form.tecnicoEmail) e.tecnicoEmail = 'Obrigatorio';
    if (!form.dataLimiteParecer) e.dataLimiteParecer = 'Obrigatorio';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setSubmitting(true);
    try {
      // Fix status values to real ones before submitting
      const payload = {
        ...form,
        statusOportunidade: '2. Viabilidade T\u00e9cnica Pendente' as OpportunityStatus,
        statusTecnico: 'N\u00e3o Iniciado' as TechnicalStatus,
      };
      await onSubmit(payload);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const borderErr = (k: string) => ({ ...inputStyle, borderColor: errors[k] ? '#EF4444' : '#252B45' });

  const MemberSelect = ({ field, nomField }: { field: 'comercialEmail' | 'tecnicoEmail'; nomField: 'comercialNome' | 'tecnicoNome' }) => (
    <select
      value={form[field]}
      disabled={loadingMembros}
      onChange={e => {
        const m = membros.find(x => x.email === e.target.value);
        set(field, e.target.value);
        if (m) set(nomField, m.nome);
      }}
      style={{ ...borderErr(field), opacity: loadingMembros ? 0.5 : 1 }}
    >
      <option value="">{loadingMembros ? 'A carregar...' : '-- Seleccionar --'}</option>
      {membros.map(m => <option key={m.email} value={m.email}>{m.nome}</option>)}
    </select>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflowY: 'auto',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#1A1F35', border: '1px solid #252B45', borderRadius: 16,
        width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #252B45', background: '#131729', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>+ Nova Oportunidade</div>
            <div style={{ fontSize: 12, color: '#8892AA', marginTop: 2 }}>Preenche todos os campos obrigatorios</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8892AA', fontSize: 22, cursor: 'pointer' }}>x</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {sectionTitle('#CC2936', '📋', 'Identificacao')}
          <div style={col2}>
            <div>
              <label style={labelStyle}>Codigo da Oportunidade</label>
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...inputStyle, borderColor: '#252B45', opacity: loadingCode ? 0.5 : 1, paddingRight: 32 }}
                  value={loadingCode ? 'A gerar...' : form.titulo}
                  readOnly
                />
                {!loadingCode && (
                  <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#22C55E' }}>✓ Auto</span>
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Fase *</label>
              <select style={inputStyle} value={form.statusOportunidade} onChange={e => set('statusOportunidade', e.target.value as OpportunityStatus)}>
                <option value="1. Levantamento Inicial">1. Levantamento Inicial</option>
                <option value="2. Viabilidade T&#233;cnica Pendente">2. Viabilidade T&#233;cnica Pendente</option>
                <option value="3. Proposta em Elabora&#231;&#227;o">3. Proposta em Elaboracao</option>
                <option value="4. Proposta Enviada">4. Proposta Enviada</option>
              </select>
            </div>
          </div>
          <div style={col2}>
            <div>
              <label style={labelStyle}>Cliente *</label>
              <input style={borderErr('cliente')} value={form.cliente} onChange={e => set('cliente', e.target.value)} placeholder="Nome da empresa" />
              {errors.cliente && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{errors.cliente}</div>}
            </div>
            <div>
              <label style={labelStyle}>Email de Contacto do Cliente</label>
              <input style={inputStyle} value={form.contactoCliente} onChange={e => set('contactoCliente', e.target.value)} placeholder="contacto@cliente.com" />
            </div>
          </div>

          {sectionTitle('#CC2936', '📝', 'Ambito e Necessidades')}
          <div>
            <label style={labelStyle}>Descricao do Ambito *</label>
            <textarea
              value={form.ambitoNecessidades}
              onChange={e => set('ambitoNecessidades', e.target.value)}
              rows={4}
              placeholder="Descreve detalhadamente o que o cliente precisa..."
              style={{ ...borderErr('ambitoNecessidades'), resize: 'vertical' }}
            />
            {errors.ambitoNecessidades && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{errors.ambitoNecessidades}</div>}
          </div>

          {sectionTitle('#1755B8', '💼', 'Responsavel Comercial')}
          <div style={col2}>
            <div>
              <label style={labelStyle}>Comercial *</label>
              <MemberSelect field="comercialEmail" nomField="comercialNome" />
              {errors.comercialEmail && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{errors.comercialEmail}</div>}
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={{ ...inputStyle, opacity: .6 }} value={form.comercialEmail} readOnly />
            </div>
          </div>

          {sectionTitle('#22C55E', '🔧', 'Responsavel Tecnico (DTI)')}
          <div style={col2}>
            <div>
              <label style={labelStyle}>Tecnico Responsavel *</label>
              <MemberSelect field="tecnicoEmail" nomField="tecnicoNome" />
              {errors.tecnicoEmail && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{errors.tecnicoEmail}</div>}
            </div>
            <div>
              <label style={labelStyle}>Data Limite Parecer *</label>
              <input
                type="date"
                value={form.dataLimiteParecer}
                onChange={e => set('dataLimiteParecer', e.target.value)}
                style={{ ...borderErr('dataLimiteParecer'), colorScheme: 'dark' }}
              />
              {errors.dataLimiteParecer && <div style={{ fontSize: 11, color: '#EF4444', marginTop: 3 }}>{errors.dataLimiteParecer}</div>}
            </div>
          </div>

          {/* Preview */}
          {form.titulo && form.cliente && (
            <div style={{ background: 'rgba(204,41,54,.06)', border: '1px solid rgba(204,41,54,.2)', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ fontSize: 11, color: '#CC2936', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Pre-visualizacao</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{form.titulo}</div>
              <div style={{ fontSize: 12, color: '#8892AA', marginTop: 3 }}>
                🏢 {form.cliente} &nbsp;·&nbsp; Tecnico: {form.tecnicoNome || '--'} &nbsp;·&nbsp; Prazo: {form.dataLimiteParecer || '--'}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid #252B45', display: 'flex', gap: 10, justifyContent: 'flex-end', background: 'rgba(0,0,0,.2)', flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: '#131729', border: '1px solid #252B45', color: '#8892AA', borderRadius: 8, padding: '10px 20px', fontSize: 13, cursor: 'pointer' }}>
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              background: submitting ? '#555' : 'linear-gradient(135deg,#CC2936,#9B1D24)',
              color: 'white', border: 'none', borderRadius: 8, padding: '10px 26px',
              fontSize: 13, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'A criar...' : 'Criar Oportunidade'}
          </button>
        </div>
      </div>
    </div>
  );
};
